/* CookieBanner — bottom slide-up GDPR/CCPA consent prompt shown to first-time
 * visitors. Persists the choice in localStorage under `zhelta-cookie-consent`
 * with `{version, choice, ts}` so a future schema change can re-prompt by
 * bumping `CONSENT_VERSION`.
 *
 * The banner is mounted globally in App.js. Strictly necessary cookies are
 * always allowed; the choice gate only governs analytics / marketing
 * categories that downstream code can read via `getCookieConsent()`.
 *
 * UX notes:
 *   • Bottom-fixed card, slides up after a short delay so it doesn't fight
 *     the homepage hero on first paint.
 *   • Three actions: Accept, Reject (essential only), and "Manage" → routes
 *     to /cookies.
 *   • Listens for `zhelta-cookie-consent-cleared` so the Cookies page can
 *     reopen the banner programmatically.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'zhelta-cookie-consent';
const CONSENT_VERSION = 1;

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed; // { version, choice: 'accepted'|'rejected', ts }
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  // Default to hidden — only render once we've confirmed there's no stored
  // choice. Avoids a flash of banner during hydration.
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const decide = () => {
      const stored = getCookieConsent();
      // Show after a brief delay so it doesn't fight first paint.
      if (!stored) {
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
      setVisible(false);
      return undefined;
    };
    const cleanup = decide();
    const onCleared = () => decide();
    window.addEventListener('zhelta-cookie-consent-cleared', onCleared);
    return () => {
      window.removeEventListener('zhelta-cookie-consent-cleared', onCleared);
      if (typeof cleanup === 'function') cleanup();
    };
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        choice,
        ts: new Date().toISOString(),
      }));
      window.dispatchEvent(new CustomEvent('zhelta-cookie-consent-changed', { detail: { choice } }));
    } catch {
      // Storage blocked — banner just won't reappear this session, which is fine.
    }
    setClosing(true);
    setTimeout(() => setVisible(false), 220);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className={`fixed inset-x-3 bottom-3 sm:inset-x-auto sm:left-4 sm:right-auto sm:bottom-4 sm:max-w-[420px] z-[80] max-h-[85vh] overflow-y-auto ${closing ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'} transition-all duration-200`}
    >
      <div
        className="rounded-2xl border border-zinc-200 bg-white text-zinc-900 shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-5 sm:p-6"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#f5a623]/15 text-[#c9a227] inline-flex items-center justify-center flex-shrink-0">
            <Cookie className="w-4.5 h-4.5" />
          </div>
          <div className="flex-1">
            <div className="text-[12.5px] uppercase tracking-[0.18em] font-extrabold text-[#a8871a] mb-1">
              Cookies on ZHELTA
            </div>
            <p className="text-[13.5px] text-zinc-600 leading-relaxed m-0">
              We use strictly necessary cookies to make this site work, plus optional analytics
              and preference cookies to improve your experience. You can change your choice any
              time from the{' '}
              <Link to="/cookies" className="underline underline-offset-2 text-zinc-900 hover:text-[#c9a227]">
                Cookie Policy
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => persist('rejected')}
            data-testid="cookie-banner-dismiss"
            aria-label="Reject non-essential cookies"
            className="text-zinc-400 hover:text-zinc-900 transition flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => persist('accepted')}
            data-testid="cookie-banner-accept"
            className="px-4 py-2 rounded-full bg-[#f5a623] hover:bg-[#e69e1f] text-black text-[12.5px] font-bold uppercase tracking-wider transition-transform hover:-translate-y-0.5"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => persist('rejected')}
            data-testid="cookie-banner-reject"
            className="px-4 py-2 rounded-full border border-zinc-300 hover:border-zinc-900 text-zinc-900 text-[12.5px] font-bold uppercase tracking-wider"
          >
            Essential only
          </button>
          <Link
            to="/cookies"
            data-testid="cookie-banner-manage"
            className="ml-auto text-[12px] text-zinc-500 hover:text-zinc-900 underline underline-offset-2"
          >
            Manage
          </Link>
        </div>
      </div>
    </div>
  );
}
